import { Link } from "wouter";
import { Shell } from "@/components/layout/Shell";
import { ARTICLE_LIST } from "@/data/insights";
import { useSeo } from "@/hooks/use-seo";
import { motion } from "framer-motion";

export default function Insights() {
  useSeo({
    title: "Color Psychology Insights — Articles & Research | Mychromamind",
    description:
      "In-depth articles on color psychology: how colors affect mood, behavior, decisions, health, and creativity. Original research-backed writing on the science of color.",
    path: "/insights",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Blog",
          name: "Mychromamind Insights",
          description:
            "Original, research-backed writing on how color shapes mood, behavior, creativity, and everyday life.",
          url: "https://mychromamind.com/insights",
          publisher: {
            "@type": "Organization",
            name: "Mychromamind",
            url: "https://mychromamind.com",
          },
        },
        {
          "@type": "ItemList",
          itemListElement: ARTICLE_LIST.map((a, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://mychromamind.com/insights/${a.slug}`,
            name: a.title,
          })),
        },
      ],
    },
  });

  return (
    <Shell>
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-20 w-full">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-serif mb-4">Color Psychology Insights</h1>
          <p className="text-muted-foreground text-lg max-w-2xl">
            Original, research-backed writing on how color shapes mood, behavior, creativity, and everyday life.
          </p>
        </motion.div>

        {/* Article Grid */}
        <div className="grid gap-6 md:grid-cols-2">
          {ARTICLE_LIST.map((article, i) => (
            <motion.article
              key={article.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
            >
              <Link href={`/insights/${article.slug}`}>
                <div className="group h-full p-6 rounded-2xl border border-border bg-card hover:border-primary/30 hover:shadow-md transition-all duration-200 cursor-pointer flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-primary/70 uppercase tracking-wider">
                      {article.category}
                    </span>
                    <span className="text-xs text-muted-foreground">{article.readTime} min read</span>
                  </div>
                  <h2 className="text-lg font-serif leading-snug group-hover:text-primary transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                    {article.description}
                  </p>
                  <span className="text-xs text-muted-foreground mt-1">{article.date}</span>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </Shell>
  );
}
